import axios from "axios";

/**
 * In-app login: authz-api /login/*. No bearer token yet — the user is signing
 * in. State between steps travels in the signed `ticket`.
 */
const http = axios.create({
  baseURL:
    process.env.REACT_APP_AUTH_URL || "https://authz-api.cms.chargingadda.com",
  timeout: 20000,
  headers: { "Content-Type": "application/json" },
});

export interface LoginOrg {
  name: string;
  logo?: string | null;
  platform_url?: string | null;
  zitadel_org_id: string;
}

export interface LoginIdp {
  id: string;
  name: string;
  /** IDENTITY_PROVIDER_TYPE_GOOGLE | _AZURE_AD | _OIDC | ... */
  type: string;
}

export interface LoginRequestInfo {
  auth_request_id: string;
  org: LoginOrg;
  login_hint?: string | null;
  /** Where the login page should run (the org's own subdomain) */
  login_url: string;
  rp_domain: string;
  /** SSO providers the org connected (Settings -> Members -> Single sign-on) */
  idps: LoginIdp[];
  /** Org sign-in policy */
  allow_password: boolean;
  password_reset: boolean;
}

export type MfaMethod = "totp" | "u2f" | "otp_sms" | "otp_email";
export type LoginNext = "password" | "mfa" | "change_password" | "setup_totp" | "done";

export interface LoginStep {
  next: LoginNext;
  ticket?: string | null;
  user?: { display_name: string; login_name: string } | null;
  mfa_methods: MfaMethod[];
  webauthn?: any;
  code_sent: boolean;
  totp_secret?: string | null;
  totp_uri?: string | null;
  callback_url?: string | null;
}

export const loginApi = {
  request: (authRequestId: string) =>
    http.get<LoginRequestInfo>(`/login/request/${encodeURIComponent(authRequestId)}`),

  password: (auth_request_id: string, login_name: string, password: string) =>
    http.post<LoginStep>("/login/password", { auth_request_id, login_name, password }),

  mfaChallenge: (ticket: string, method: MfaMethod) =>
    http.post<LoginStep>("/login/mfa/challenge", { ticket, method }),

  mfaVerify: (ticket: string, method: MfaMethod, payload: { code?: string; credential?: any }) =>
    http.post<LoginStep>("/login/mfa/verify", { ticket, method, ...payload }),

  changePassword: (ticket: string, new_password: string) =>
    http.post<LoginStep>("/login/change-password", { ticket, new_password }),

  setupTotp: (ticket: string) => http.post<LoginStep>("/login/setup-totp", { ticket }),
  setupTotpVerify: (ticket: string, code: string) =>
    http.post<LoginStep>("/login/setup-totp/verify", { ticket, code }),

  idpStart: (auth_request_id: string, idp_id: string) =>
    http.post<{ auth_url: string }>("/login/idp/start", { auth_request_id, idp_id }),
  idpFinish: (auth_request_id: string, intent_id: string, intent_token: string) =>
    http.post<LoginStep>("/login/idp/finish", { auth_request_id, intent_id, intent_token }),

  forgot: (login_name: string, auth_request_id?: string) =>
    http.post<{ message: string }>("/login/forgot", { login_name, auth_request_id }),

  reset: (user_id: string, code: string, new_password: string) =>
    http.post<{ message: string }>("/login/reset", { user_id, code, new_password }),
};

/** Message from an authz-api login error; `restart` = the attempt is dead. */
export function loginErr(err: any, fallback: string): { message: string; restart: boolean } {
  const d = err?.response?.data?.detail;
  if (!d) {
    return {
      message: err?.response ? fallback : "Cannot reach the sign-in service. Check your connection.",
      restart: false,
    };
  }
  if (typeof d === "string") return { message: d, restart: false };
  if (Array.isArray(d)) {
    const msgs = d.map((e: any) => (typeof e?.msg === "string" ? e.msg.replace(/^Value error, /, "") : "")).filter(Boolean);
    return { message: msgs.join(". ") || fallback, restart: false };
  }
  return { message: d.message || fallback, restart: !!d.restart };
}
